/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { QueryKey, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useDispatch } from 'react-redux';
import type { UseMutationReturnType } from '@opengeoweb/api';
import {
  optimisticUpdate,
  useAuthenticationContext,
  useAuthQuery,
} from '@opengeoweb/api';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import { uiActions, uiTypes } from '@opengeoweb/store';
import {
  deleteDrawing,
  deleteWarning,
  deleteWarningEvent,
  getAreaKeywords,
  getDrawingDetails,
  getDrawings,
  getDrawingsPredefinedAreas,
  getMaritimeWarningsConfig,
  getPublicWarningById,
  getPublicWarningList,
  getWarningEventList,
  getWarningEventsConfig,
  postMaritimeWarnings,
  postPublicWarning,
  postWarningEvent,
  saveDrawingAs,
  updateDrawing,
  updateMaritimeWarningsEditor,
  updatePublicWarning,
  updatePublicWarningEditor,
  updateWarningEvent,
  updateWarningEventEditor,
} from './api';
import type { TimeFilter } from '../../components/FilterList/filter-utils';
import {
  getApiParams,
  TimeFilterMode,
} from '../../components/FilterList/filter-utils';
import type { MaritimeWarningsConfig } from '../../components/MaritimeWarningsForm/types';
import { warningsDrawingsActions } from '../../store';
import type {
  DrawingFromBE,
  DrawingFromFE,
  DrawingListItem,
  FetchDrawingParams,
} from '../../store/warningsDrawings/types';
import { drawingDialogType } from '../../store/warningsDrawings/utils';
import { warningsEventFormActions } from '../../store/warningsEventForm';
import { warningsFormActions } from '../../store/warningsForm/reducer';
import type {
  MaritimeWarning,
  MaritimeWarningsType,
  PublicWarning,
  WarningEvent,
  WarningEventsConfig,
} from '../../store/warningsForm/types';
import { PublicWarningStatus } from '../../store/warningsForm/types';
import {
  publicWarningDialogType,
  warningEventDialogType,
} from '../../store/warningsForm/utils';
import { getIdFromUrl } from '../../utils/warningUtils';

const getSnackbarMessage = (
  objectName: string,
  wasUpdate: boolean,
): snackbarTypes.TranslatableMessage => ({
  type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
  key: wasUpdate ? 'update-succes' : 'save-succes',
  params: { objectName },
});

/**
 * @param disabled - Disables fetching, but still returns cached values.
 * https://tanstack.com/query/v4/docs/framework/react/guides/disabling-queries
 */
export const usePublicWarnings = (
  disabled = false,
  timeFilter?: TimeFilter,
): UseQueryResult<(PublicWarning | MaritimeWarning)[]> => {
  const shouldFilter = timeFilter && timeFilter.mode !== TimeFilterMode.all;
  const apiParams = shouldFilter ? getApiParams(timeFilter) : {};

  const queryKey = apiParams
    ? ['public-warning-list', JSON.stringify(apiParams)]
    : ['public-warning-list'];

  return useAuthQuery(
    queryKey,
    async (auth) => getPublicWarningList(auth, apiParams),
    disabled,
    1000 * 60,
  );
};

type MutationReturnType<T> = UseMutationReturnType<
  Headers | null,
  T,
  { previousData: [QueryKey, PublicWarning[] | undefined][] }
>;

type DrawingMutationReturnType<T> = UseMutationReturnType<
  { id: string },
  T,
  void
>;

interface MutateEditorProps {
  id: string;
  isEditor: boolean;
  force?: boolean;
}
export const useMutatePublicWarningEditor =
  (): MutationReturnType<MutateEditorProps> => {
    const queryClient = useQueryClient();
    const { auth } = useAuthenticationContext();
    const dispatch = useDispatch();
    return useMutation({
      mutationFn: async ({ id, isEditor, force }) => {
        if (!auth) {
          throw new Error('Authentication context not found');
        }
        dispatch(warningsFormActions.removeFormError());
        return updatePublicWarningEditor(id, isEditor, auth, force);
      },
      ...optimisticUpdate<PublicWarning, MutateEditorProps>(
        ['public-warning-list'],
        queryClient,
        (warning, { isEditor }) => ({
          ...warning,
          editor: isEditor ? auth!.username : undefined,
        }),
        (error) => {
          if (error.cause !== 409) {
            dispatch(
              warningsFormActions.setFormError({ message: error.message }),
            );
          }
        },
      ),
      onSuccess: async (data, { isEditor }) => {
        dispatch(
          snackbarActions.openSnackbar({
            type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
            key: isEditor
              ? 'warning-editor-add-editor'
              : 'warning-editor-remove-editor',
          }),
        );
      },
    });
  };
interface UpdateWarningProps {
  id?: string;
  data: PublicWarning;
  panelId?: string;
}

export const useMutateWarning = (
  snackBarMessage: string,
): MutationReturnType<UpdateWarningProps> => {
  const queryClient = useQueryClient();
  const { auth } = useAuthenticationContext();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      dispatch(warningsFormActions.removeFormError());
      if (id) {
        return updatePublicWarning(id, auth, data);
      }
      return postPublicWarning(auth, data);
    },
    onError: (error: Error) => {
      void queryClient.invalidateQueries({
        queryKey: ['public-warning-list'],
        exact: false,
      });

      dispatch(warningsFormActions.setFormError({ message: error.message }));
    },
    onSuccess: async (data: Headers | null, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['public-warning-list'],
        exact: false,
      });

      const location = data?.get('location');
      const { warningDetail } = variables.data;

      if (location) {
        // When making a new warning we get it's ID from the location header
        const newId = getIdFromUrl(location);
        const newWarning = {
          ...variables.data,
          id: newId,
          lastUpdatedTime: new Date().toISOString(),
          warningDetail: {
            ...warningDetail,
            id: newId,
            domain: 'public',
          },
        } as PublicWarning;
        dispatch(
          warningsFormActions.openPublicWarningFormDialog({
            warning: newWarning,
            formState: '',
            panelId: variables.panelId,
          }),
        );
      }
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
          key: snackBarMessage,
        }),
      );
      if (variables.data.status === PublicWarningStatus.PUBLISHED) {
        dispatch(
          uiActions.setToggleOpenDialog({
            type: publicWarningDialogType,
            setOpen: false,
          }),
        );
      }
    },
  });
};

// Returns a function that fetches one public warning when called. Use it where
// the id is only known later, e.g. in a click handler; a query hook such as
// useAuthQuery needs the id up front, during render.
export const useFetchPublicWarning = (): ((
  id: string,
) => Promise<PublicWarning>) => {
  const queryClient = useQueryClient();
  const { auth } = useAuthenticationContext();
  return async (id: string): Promise<PublicWarning> =>
    queryClient.fetchQuery({
      queryKey: ['public-warning', id],
      queryFn: async () => getPublicWarningById(id, auth!),
    });
};

export const useMutateDeleteWarning = (): MutationReturnType<{
  id: string;
}> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { auth } = useAuthenticationContext();
  return useMutation({
    mutationFn: async ({ id }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      return deleteWarning(id, auth);
    },
    onSuccess: async (_, variables) => {
      void queryClient.cancelQueries({
        queryKey: ['public-warning-list'],
      });
      queryClient.setQueriesData(
        { queryKey: ['public-warning-list'] },
        (old: PublicWarning[] | undefined) => {
          return old?.filter((warning) => warning.id !== variables.id);
        },
      );
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
          key: t('warning-dialog-delete-succes'),
        }),
      );
    },
  });
};

export const usePublishWarning = (): MutationReturnType<UpdateWarningProps> =>
  useMutateWarning('warning-publish-succes');

export const useSaveWarning = (): MutationReturnType<UpdateWarningProps> =>
  useMutateWarning('warning-save-succes');

// Drawings
export const useDrawings = (
  params: FetchDrawingParams = {},
  enabled = true,
): UseQueryResult<DrawingListItem[]> => {
  return useAuthQuery<DrawingListItem[]>(
    ['drawings', JSON.stringify(params)],
    async (auth) => getDrawings(auth, params),
    !enabled,
  );
};

export const useDrawingsPredefinedAreas = (
  enabled = true,
): UseQueryResult<DrawingListItem[]> => {
  return useAuthQuery<DrawingListItem[]>(
    ['drawings', 'predefinedAreas'],
    async (auth) => getDrawingsPredefinedAreas(auth),
    !enabled,
  );
};

export const useDrawingDetails = (
  drawingId: string,
): UseQueryResult<DrawingFromBE> => {
  return useAuthQuery(
    ['drawing-details', drawingId],
    async (auth) => getDrawingDetails(auth, drawingId),
    !drawingId,
  );
};

export const useAreaKeywords = (enabled = true): UseQueryResult<string[]> => {
  return useAuthQuery(['area-keywords'], getAreaKeywords, !enabled);
};

export const useMutateSaveOrUpdateDrawing = (): DrawingMutationReturnType<{
  data: DrawingFromFE;
  id?: string;
}> => {
  const { auth } = useAuthenticationContext();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ data, id }) => {
      if (!auth) {
        throw new Error('Auth context missing');
      }

      if (id) {
        await updateDrawing(auth, id, data);
        return { id };
      }

      const headers = await saveDrawingAs(auth, data);
      const location = headers.get('Location');

      if (!location) {
        throw new Error('No Location header returned from saveDrawingAs');
      }

      const newId = getIdFromUrl(location);
      return { id: newId };
    },
    onSuccess: ({ id }, variables) => {
      const idToInvalidate = variables.id ?? id;

      dispatch(
        warningsDrawingsActions.setDrawValues({
          drawingInstanceId: drawingDialogType,
          id: String(id),
          objectName: variables.data.objectName,
        }),
      );

      dispatch(
        snackbarActions.openSnackbar(
          getSnackbarMessage(variables.data.objectName, !!variables.id),
        ),
      );

      void queryClient.invalidateQueries({ queryKey: ['drawings'] });
      void queryClient.invalidateQueries({
        queryKey: ['drawing-details', idToInvalidate],
      });
    },
  });
};

interface DeleteDrawingParams {
  drawingId: string;
  objectName: string;
}

export const useMutateDeleteDrawing = (): UseMutationReturnType<
  { id: string },
  DeleteDrawingParams
> => {
  const queryClient = useQueryClient();
  const { auth } = useAuthenticationContext();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({ drawingId }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      await deleteDrawing(auth, drawingId);
      return { id: drawingId };
    },
    onSuccess: (_, { objectName }) => {
      void queryClient.invalidateQueries({ queryKey: ['drawings'] });
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
          key: 'delete-succes',
          params: { objectName },
        }),
      );
    },
  });
};

export const BE_EDITOR_WARNING =
  'Warning is being edited by another user and cannot be updated';

export const usePostMaritimeWarnings = (): MutationReturnType<
  MaritimeWarning[]
> => {
  const { auth } = useAuthenticationContext();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (data: MaritimeWarning[]) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      return postMaritimeWarnings(auth, data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['public-warning-list'],
      });
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
          key: 'maritime-warning-post-success',
        }),
      );
    },
    onError: (error: Error) => {
      if (error.message === BE_EDITOR_WARNING) {
        // only in case of an editor conflict, we need to update the list
        void queryClient.invalidateQueries({
          queryKey: ['public-warning-list'],
        });
      }
    },
  });
};

interface MutateMaritimeEditorParams {
  domain: MaritimeWarningsType;
  isEditor: boolean;
  force?: boolean;
}
export const useMutateMaritimeWarningsEditor =
  (): MutationReturnType<MutateMaritimeEditorParams> => {
    const { auth } = useAuthenticationContext();

    return useMutation({
      mutationFn: async ({ domain, isEditor, force }) => {
        if (!auth) {
          throw new Error('Authentication context not found');
        }
        return updateMaritimeWarningsEditor(domain, isEditor, auth, force);
      },
    });
  };

export const useMaritimeWarningsConfig = (
  disabled = false,
): UseQueryResult<MaritimeWarningsConfig> => {
  const maritimeConfigQuery = useAuthQuery(
    ['maritime-warnings-config'],
    getMaritimeWarningsConfig,
    disabled,
  );

  return maritimeConfigQuery;
};

export const useWarningEventsConfig = (
  disabled = false,
): UseQueryResult<WarningEventsConfig> =>
  useAuthQuery(['warning-events-config'], getWarningEventsConfig, disabled);

export const useWarningEventsList = (
  disabled = false,
  timeFilter?: TimeFilter,
): UseQueryResult<WarningEvent[]> => {
  const apiParams = timeFilter ? getApiParams(timeFilter) : {};

  return useAuthQuery(
    ['warning-event-list', JSON.stringify(apiParams)],
    async (auth) => getWarningEventList(auth, apiParams),
    disabled,
    1000 * 60,
  );
};

type EventMutationReturnType<T> = UseMutationReturnType<
  Headers | null,
  T,
  { previousData: [QueryKey, WarningEvent[] | undefined][] }
>;
interface UpdateWarningEventProps {
  eventId?: string;
  data: WarningEvent;
  panelId?: string;
}

export const usePublishWarningEvent =
  (): EventMutationReturnType<UpdateWarningEventProps> =>
    useMutateWarningEvent('warning-event-publish-succes');

export const useSaveWarningEvent =
  (): EventMutationReturnType<UpdateWarningEventProps> =>
    useMutateWarningEvent('warning-event-save-succes');

export const useMutateWarningEvent = (
  snackBarMessage: string,
): EventMutationReturnType<UpdateWarningEventProps> => {
  const queryClient = useQueryClient();
  const { auth } = useAuthenticationContext();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({ eventId, data }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      dispatch(warningsEventFormActions.removeFormError());
      if (eventId) {
        return updateWarningEvent(eventId, auth, data);
      }
      return postWarningEvent(auth, data);
    },
    onError: (error: Error) => {
      void queryClient.invalidateQueries({
        queryKey: ['warning-event-list'],
        exact: false,
      });
      dispatch(
        warningsEventFormActions.setFormError({ message: error.message }),
      );
    },
    onSuccess: async (data: Headers | null, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['warning-event-list'],
        exact: false,
      });

      const location = data?.get('location');
      if (location) {
        // When making a new event we get its ID from the location header
        const newId = getIdFromUrl(location);

        const newEvent = {
          ...variables.data,
          eventId: newId,
          lastUpdatedTime: new Date().toISOString(),
        } as WarningEvent;
        dispatch(
          warningsEventFormActions.openWarningsEventFormDialog({
            event: newEvent,
            formState: '',
            panelId: variables.panelId,
          }),
        );
      }
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
          key: snackBarMessage,
        }),
      );
      if (variables.data.status === PublicWarningStatus.PUBLISHED) {
        dispatch(
          uiActions.setToggleOpenDialog({
            type: warningEventDialogType,
            setOpen: false,
          }),
        );
        dispatch(
          uiActions.setToggleOpenDialog({
            type: uiTypes.DialogTypes.AreaDrawingLoader,
            setOpen: false,
          }),
        );
      }
    },
  });
};

export const useMutateDeleteWarningEvent = (): EventMutationReturnType<{
  eventId: string;
}> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { auth } = useAuthenticationContext();
  return useMutation({
    mutationFn: async ({ eventId }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      return deleteWarningEvent(eventId, auth);
    },
    onSuccess: async (_, variables) => {
      void queryClient.cancelQueries({
        queryKey: ['warning-event-list'],
      });
      queryClient.setQueriesData(
        { queryKey: ['warning-event-list'] },
        (old: WarningEvent[] | undefined) => {
          return old?.filter((event) => event.eventId !== variables.eventId);
        },
      );
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
          key: t('warning-dialog-delete-succes'),
        }),
      );
    },
  });
};

interface MutateEventEditorProps {
  eventId: string;
  isEditor: boolean;
  force?: boolean;
}
export const useMutateEventEditor =
  (): EventMutationReturnType<MutateEventEditorProps> => {
    const queryClient = useQueryClient();
    const { auth } = useAuthenticationContext();
    const dispatch = useDispatch();
    return useMutation({
      mutationFn: async ({ eventId, isEditor, force }) => {
        if (!auth) {
          throw new Error('Authentication context not found');
        }
        dispatch(warningsEventFormActions.removeFormError());
        return updateWarningEventEditor(eventId, isEditor, auth, force);
      },
      ...optimisticUpdate<WarningEvent, MutateEventEditorProps>(
        ['warning-event-list'],
        queryClient,
        (event, { isEditor }) => ({
          ...event,
          editor: isEditor ? auth!.username : undefined,
        }),
        (error) => {
          if (error.cause !== 409) {
            dispatch(
              warningsEventFormActions.setFormError({ message: error.message }),
            );
          }
        },
      ),
      onSuccess: async (data, { isEditor }) => {
        dispatch(
          snackbarActions.openSnackbar({
            type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
            key: isEditor
              ? 'warning-event-editor-add-editor'
              : 'warning-event-editor-remove-editor',
          }),
        );
      },
    });
  };
